import React from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { StarIcon, XIcon } from "lucide-react";
import { toast } from "sonner";
import type { OnboardingAnswers } from "../../../types/onboarding";
import { availablePairs } from "../../../data/languages";
import { MAX_TARGETS } from "../../../data/onboarding";
import { LanguageGrid } from "../../courses/LanguageGrid";
import { LangChip } from "../../ui/LangChip";
import { StepHeading } from "../StepHeading";
import { getLanguage, targetsFrom } from "../../../utils/language";
import { spring } from "../../../utils/motion";
interface LanguageStepProps {
  answers: OnboardingAnswers;
  update: (p: Partial<OnboardingAnswers>) => void;
}
export function LanguageStep({
  answers,
  update
}: LanguageStepProps) {
  const reduce = useReducedMotion() ?? false;
  const bases = availablePairs.map(p => p.from);
  const available = targetsFrom(answers.base);

  /** Keeps `target` (the main language) in sync, and resets checks that were about the old main language. */
  const setTargets = (targets: string[]) => {
    const mainChanged = targets[0] !== answers.target;
    update({
      targets,
      target: targets[0],
      ...(mainChanged ? {
        canDo: [],
        quiz: []
      } : {})
    });
  };
  const toggle = (code: string) => {
    if (answers.targets.includes(code)) {
      setTargets(answers.targets.filter(t => t !== code));
      return;
    }
    if (answers.targets.length >= MAX_TARGETS) {
      toast("Up to three languages for now", {
        description: "You can add more courses any time from the app."
      });
      return;
    }
    setTargets([...answers.targets, code]);
  };
  const makeMain = (code: string) => setTargets([code, ...answers.targets.filter(t => t !== code)]);
  const changeBase = (code: string) => {
    const allowed = targetsFrom(code);
    update({
      base: code
    });
    setTargets(answers.targets.filter(t => allowed.includes(t)));
  };
  return <div data-id="element-965" data-mp="y3u97rgii6tq">
      <StepHeading title="Which languages?" description="Each course is a pair: the language you learn, explained in one you already know. Learning more than one? Pick up to three." data-id="element-966" data-mp="2v2lzp4ln7gmd" />

      <fieldset data-id="element-967" data-mp="343202573a7n0">
        <legend className="text-sm font-bold text-ink" data-id="element-968" data-mp="343ttb9d7rta4">I speak</legend>
        <div className="no-scrollbar -mx-1 mt-2.5 flex gap-2 overflow-x-auto px-1 pb-1" role="radiogroup" aria-label="I speak" data-id="element-969" data-mp="2z47jzb0iax6l">
          {bases.map(code => {
          const active = code === answers.base;
          return <button key={code} type="button" role="radio" aria-checked={active} onClick={() => changeBase(code)} className={`focus-ring relative shrink-0 whitespace-nowrap rounded-xl px-3.5 py-2 text-sm font-semibold transition-colors duration-150 ${active ? "text-paper" : "border-2 border-line/30 bg-surface text-ink-2 hover:text-ink"}`} data-id="element-970" data-mp="27dufp0i7i4ko">
                {active && <motion.span layoutId="ob-base" transition={spring.snappy} className="absolute inset-0 rounded-xl bg-ink" aria-hidden="true" data-id="element-971" data-mp="35yliwt8pov8" />}
                <span className="relative" lang={code} data-id="element-972" data-mp="7zuh5sm16zn8">
                  {getLanguage(code).native}
                </span>
              </button>;
        })}
        </div>
      </fieldset>

      <fieldset className="mt-7" data-id="element-973" data-mp="3v8pnbd0dhigg">
        <legend className="text-sm font-bold text-ink" data-id="element-974" data-mp="muh6t3b4ki4n">
          I want to learn{" "}
          <span className="font-medium text-ink-2" data-id="element-975" data-mp="qwc31te785bk">
            · {answers.targets.length} of {MAX_TARGETS} picked
          </span>
        </legend>

        {/* Picked languages, in order. The first one is the main language for the level check. */}
        <ul className="mt-2.5 flex min-h-[44px] flex-wrap gap-2" aria-label="Picked languages" data-id="element-976" data-mp="hucnz6mqvbb9">
          <AnimatePresence initial={false} data-id="element-977" data-mp="2xlgzhbcwfxba">
            {answers.targets.length === 0 && <motion.li key="empty" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="self-center text-sm text-ink-2" data-id="element-978" data-mp="32mxy107wv7op">
                Nothing picked yet — choose below.
              </motion.li>}
            {answers.targets.map((code, i) => <motion.li key={code} layout={!reduce} initial={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            transition: {
              duration: 0.12
            }
          }} transition={spring.snappy} className={`flex items-center gap-2 rounded-xl border-3 py-1 pl-1.5 pr-1 ${i === 0 ? "border-line bg-accent/60" : "border-line/40 bg-surface"}`} data-id="element-979" data-mp="3949ia63ko5e9" data-mpi={i}>
                <LangChip code={code} size="sm" tone={i === 0 ? "surface" : "tint"} data-id="element-980" data-mp="3mu392tyn0zqm" />
                <span className="text-sm font-semibold text-ink" data-id="element-981" data-mp="2gfu2hhcm9j1v">{getLanguage(code).name}</span>
                {i === 0 ? <span className="flex items-center gap-1 rounded-md bg-surface/80 px-1.5 py-0.5 text-[11px] font-bold text-ink" data-id="element-982" data-mp="2beekjy5qjsnp">
                    <StarIcon className="h-3 w-3 fill-current" aria-hidden="true" data-id="element-983" data-mp="26avmvhd9n5le" /> Main
                  </span> : <button type="button" onClick={() => makeMain(code)} className="focus-ring rounded-md px-1.5 py-0.5 text-[11px] font-bold text-ink-2 underline underline-offset-2 hover:text-ink" data-id="element-984" data-mp="2b8xfaswl0h9j">
                    Make main
                  </button>}
                <button type="button" onClick={() => toggle(code)} aria-label={`Remove ${getLanguage(code).name}`} className="focus-ring grid h-7 w-7 place-items-center rounded-lg text-ink-2 hover:bg-sunken hover:text-ink" data-id="element-985" data-mp="2p36l2xwz6fd6">
                  <XIcon className="h-3.5 w-3.5" data-id="element-986" data-mp="14liagve7be0g" />
                </button>
              </motion.li>)}
          </AnimatePresence>
        </ul>
        {answers.targets.length > 1 && <p className="mt-2 text-xs text-ink-2" data-id="element-987" data-mp="rlr4zfxbyg02">We'll check your level in your main language. For the others, a quick self-rating is enough.</p>}

        <div className="mt-4 max-h-[42vh] overflow-y-auto rounded-card pr-1" data-id="element-988" data-mp="16kl5p5dlx79p">
          <LanguageGrid key={answers.base} codes={available} selectedCodes={answers.targets} columns={2} searchLabel="Search languages" onSelect={toggle} data-id="element-989" data-mp="11ivqren9k9gc" />
        </div>
      </fieldset>
    </div>;
}